import { useMemo } from "react";
import {
  Box,
  SidebarSection,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { IssueMenuItems } from "#app/components/issue-actions/issue-menu";
import { useIssueView } from "#app/components/issue-view";
import type { IssueRow } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { isAddressLike, streetLine } from "#app/lib/utils/format-address";
import {
  type FavoriteSidebarRow,
  useFavoriteIssues,
} from "#app/lib/hooks/use-favorite-issues";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useCloseSidebarIfCompact } from "#app/lib/mobile-sidebar-context";
import { issueHref } from "#app/lib/utils/entity-hrefs";
import { FavoriteItem } from "../favorite-item";

/**
 * One favorited project row. Its leading visual is the project's STAGE: every
 * row in the section is a project, so the glyph spends itself on where that
 * project stands and the list reads as a live pipeline. The tooltip carries
 * what the pie cannot — the stage name, and the workflow it belongs to, since
 * favorites span workflows and two of them can each own a "Signed".
 *
 * The ⋯ and right-click menus are the SAME roster a board card and a table row
 * open, wired off `issue` — the live row. A mirrored row (the frame before the
 * fetch lands) carries no viewer access, so it keeps the two actions the row
 * can serve on its own rather than offering mutations off stale data.
 */
function FavoriteIssueRow({
  row,
  issue,
  onRemove,
}: {
  row: FavoriteSidebarRow;
  issue: IssueRow | undefined;
  onRemove: (issueId: string) => void;
}) {
  const { activeWorkspace } = useWorkspace();
  const { stageMap, workflowMap } = useWorkspaceData();
  const { openIssueById } = useIssueView();
  const closeDrawer = useCloseSidebarIfCompact();

  const stage = stageMap.get(row.stageId);
  const workflowName = workflowMap.get(row.workflowId)?.name;
  // A project named by its address ("4869 W Malad St, Boise, ID 83705, USA")
  // truncates at the one part that tells it apart; the row shows the street
  // line and the tooltip carries the whole address.
  const addressName = isAddressLike(row.name);

  return (
    <FavoriteItem
      actionLabel="Project actions"
      copyUrl={issueHref(
        activeWorkspace?.urlPath ?? "",
        row.workflowId,
        row.issueId,
      )}
      icon={ENTITY_KIND_ICON.issue}
      id={row.issueId}
      isActive={false}
      items={
        issue
          ? (parts) => (
              <IssueMenuItems
                issue={issue}
                parts={parts}
                readOnly={!issue.viewerCanEdit}
              />
            )
          : undefined
      }
      name={addressName ? streetLine(row.name) : row.name}
      onClick={() => {
        closeDrawer();
        openIssueById(row.issueId, { source: "sidebar_favorites" });
      }}
      tooltip={addressName ? row.name : undefined}
      onRemove={onRemove}
      visual={
        stage ? (
          <Tooltip>
            <TooltipTrigger render={<Box align="center" direction="row" />}>
              <EntityTag
                display="visual"
                entity={{ id: row.stageId, kind: "stage", stage }}
                ghost={false}
                interactive={false}
              />
            </TooltipTrigger>
            <TooltipPopup side="right">
              <Box gap="2xs">
                <Text size="xs">{stage.name}</Text>
                {workflowName ? (
                  <Text size="2xs">{workflowName} workflow</Text>
                ) : null}
              </Box>
            </TooltipPopup>
          </Tooltip>
        ) : undefined
      }
    />
  );
}

/**
 * The favorited-issue rows, section-less so a mixed-kind section (Home's
 * Favorites) can seat them beside Account and Contact rows. Rows are the
 * mirror-backed `sidebarRows`, so they paint on the shell's first frame
 * instead of popping in after the preferences fetch; the live rows are matched
 * back by id to wire each row's menus.
 */
export function FavoriteIssueRows() {
  const { sidebarRows: favoriteIssues, issues, removeFavorite } =
    useFavoriteIssues();

  const liveById = useMemo(
    () => new Map(issues.map((issue) => [issue.issueId, issue])),
    [issues],
  );

  return (
    <>
      {favoriteIssues.map((row: FavoriteSidebarRow) => (
        <FavoriteIssueRow
          issue={liveById.get(row.issueId)}
          key={row.issueId}
          onRemove={removeFavorite}
          row={row}
        />
      ))}
    </>
  );
}

/**
 * "Favorites" module-menu section — the user's favorited issues. Used by the
 * Workflows module menu (Home composes `HomeFavoritesSection`, which adds the
 * Account and Contact rows). Renders nothing when there are no favorites;
 * instances also drop it from their section list in that case so ModuleMenu
 * doesn't draw a stray divider.
 */
export function FavoritesSection() {
  const { sidebarRows: favoriteIssues } = useFavoriteIssues();

  if (favoriteIssues.length === 0) return null;

  return (
    <SidebarSection title="Favorites">
      <FavoriteIssueRows />
    </SidebarSection>
  );
}
